import type { RequestInitiator } from '@ms/yammer-telemetry-support';

import { getExperimentTreatmentIfActive } from '../domains/experiments/selectors';
import { isFeatureRolloutEnabled } from '../domains/featureRollouts/selectors';
import { experimentConfigs } from '../state/ecsExperiments/types';
import type { EcsProjectTeamName, Experiment, FeatureRollout, YammerState } from '../state/types';

import { executeRequest, getResponseBody } from './apiRequest';
import { GraphqlApiError } from './errors';
import type {
  ApiHeaders,
  ApiRequest,
  ApiResponse,
  GraphqlApiRequest,
  GraphqlApiRequestBody,
  GraphqlApiTreatmentRecord,
} from './types';

const addTreatmentsToBody = <T>(
  body: GraphqlApiRequestBody<T>,
  treatments?: GraphqlApiTreatmentRecord[]
): GraphqlApiRequestBody<T> => {
  if (treatments && treatments.length > 0) {
    const existingTreatments = body.extensions?.yammerTreatments?.treatments ?? [];

    return {
      ...body,
      extensions: {
        ...body.extensions,
        yammerTreatments: {
          version: '1',
          treatments: [...existingTreatments, ...treatments],
        },
      },
    };
  }

  return body;
};

export interface AddTreatmentsToRequestOptions<T> {
  readonly request: GraphqlApiRequest<T>;
  readonly treatments?: GraphqlApiTreatmentRecord[];
}
type AddTreatmentsToRequest = <T>(options: AddTreatmentsToRequestOptions<T>) => GraphqlApiRequest<T>;
export const addTreatmentsToRequest: AddTreatmentsToRequest = ({ request, treatments }) => ({
  ...request,
  body: addTreatmentsToBody(request.body, treatments),
});

export interface GetGraphqlApiRequestOptions<T> {
  readonly body: GraphqlApiRequestBody<T>;
  readonly initiator?: RequestInitiator;
  readonly treatments?: GraphqlApiTreatmentRecord[];
  readonly headers?: ApiHeaders;
}
type GetGraphqlApiRequest = <T>(options: GetGraphqlApiRequestOptions<T>) => GraphqlApiRequest<T>;
export const getGraphqlApiRequest: GetGraphqlApiRequest = ({ body, initiator, treatments, headers }) => ({
  path: '/graphql',
  method: 'POST',
  body: addTreatmentsToBody(body, treatments),
  apiName: body.operationName,
  requestType: 'Graphql',
  credentials: 'omit',
  initiator,
  headers,
});

interface GetTreatmentsExtensionForGraphqlBodyOptions {
  readonly state: YammerState;
  readonly experiments?: Experiment[];
  readonly featureRollouts?: FeatureRollout[];
}
type GetTreatmentsExtensionForGraphqlBody = (
  options: GetTreatmentsExtensionForGraphqlBodyOptions
) => GraphqlApiTreatmentRecord[];
export const getTreatmentsExtensionForGraphqlBody: GetTreatmentsExtensionForGraphqlBody = ({
  state,
  experiments = [],
  featureRollouts = [],
}) => {
  const experimentTreatmentRecords: GraphqlApiTreatmentRecord[] = experiments
    .map((experiment) => ({
      project: experimentConfigs[experiment].projectTeam,
      key: experiment,
      value: getExperimentTreatmentIfActive(state, experiment)?.toString() || '',
    }))
    .filter((experiment) => experiment.value);

  const project: EcsProjectTeamName = 'YammerClients';
  const featureRolloutTreatmentRecords: GraphqlApiTreatmentRecord[] = featureRollouts
    .map((featureRollout) => ({
      project,
      key: featureRollout,
      value: isFeatureRolloutEnabled(state, featureRollout) ? 'true' : '',
    }))
    .filter((featureRollout) => featureRollout.value);

  return [...experimentTreatmentRecords, ...featureRolloutTreatmentRecords];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ProcessGraphqlResponse = (request: ApiRequest, response: Response) => Promise<any>;
const processGraphqlResponse: ProcessGraphqlResponse = async (request: ApiRequest, response: Response) => {
  const body = await getResponseBody(response);

  /*
    This is a temporary fix for system message issue. See more details: https://dev.azure.com/yammer/engineering/_workitems/edit/312266
    We will remove this once we have a proper BE fix.
  */
  if (body.errors && body.errors[0].message === 'Group not found' && body.errors.length === 1) {
    return body;
  }

  /*
    Allow partial responses when storylineOnlyForUsersInOfficeGroup fails to load. Temporary fix to unblock customer
    The group may have been deleted or is inaccessible, but we still want to load the rest of the settings.
  */
  if (
    body.errors &&
    body.errors[0]?.path?.includes('storylineOnlyForUsersInOfficeGroup') &&
    body.data?.settings?.storyline
  ) {
    return body;
  }

  if (body.errors && body.errors[0]) {
    throw new GraphqlApiError(request, body.errors[0], response.status, response.headers);
  }

  if (response.ok) {
    return body;
  }

  throw new GraphqlApiError(request, response.statusText, response.status, response.headers);
};

type InvokeApiRequest = <TResponse>(request: ApiRequest) => Promise<ApiResponse<TResponse>>;
const graphqlApiRequest: InvokeApiRequest = async (request) => {
  try {
    const response = await executeRequest(request);
    const body = await processGraphqlResponse(request, response);

    return { body, headers: response.headers };
  } catch (error) {
    if (error instanceof GraphqlApiError) {
      throw error;
    }

    throw new GraphqlApiError(request, error);
  }
};

type InvokeGraphqlApiRequest = <TResponse>(request: GraphqlApiRequest<unknown>) => Promise<ApiResponse<TResponse>>;

export const graphqlApiRequestWithoutAutomaticPersistedQueries: InvokeGraphqlApiRequest = async (request) => {
  const {
    body: {
      query: { contents },
      ...bodyWithoutQuery
    },
    ...requestWithoutBody
  } = request;
  const requestWithQuery = {
    ...requestWithoutBody,
    body: {
      ...bodyWithoutQuery,
      query: contents,
    },
  };

  return graphqlApiRequest(requestWithQuery);
};

export const graphqlApiRequestWithAutomaticPersistedQueries: InvokeGraphqlApiRequest = async (request) => {
  const {
    body: {
      query: { contents, sha256Hash },
      extensions: originalExtensions,
      ...bodyWithoutQuery
    },
    ...requestWithoutBody
  } = request;
  const extensions = {
    ...originalExtensions,
    persistedQuery: {
      version: 1,
      sha256Hash,
    },
  };
  const requestWithoutQuery = {
    ...requestWithoutBody,
    body: {
      ...bodyWithoutQuery,
      extensions,
    },
  };
  const requestWithQuery = {
    ...requestWithoutBody,
    body: {
      ...bodyWithoutQuery,
      query: contents,
      extensions,
    },
  };

  try {
    return await graphqlApiRequest(requestWithoutQuery);
  } catch (error) {
    if (error.message === 'PersistedQueryNotFound') {
      return graphqlApiRequest(requestWithQuery);
    }

    throw error;
  }
};
